import { useMemo, useState } from "react";
import type { MouseEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, usePaginatedQuery } from "convex/react";
import { api as convexApi } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type { SessionHistoryItem, SessionLifecycleStatus } from "../../shared/types.ts";
import { useRoleAuth } from "../auth/AuthGate.tsx";
import { SignOutButton } from "../auth/SignOutButton.tsx";
import { notificationCopy, sessionsDeletedCopy } from "../lib/notificationCopy.ts";
import { notify } from "../lib/notify.ts";

type SessionStatusFilter = "all" | SessionLifecycleStatus;
type PaginatedHistory = {
  results: SessionHistoryItem[];
  status: "LoadingFirstPage" | "CanLoadMore" | "LoadingMore" | "Exhausted";
  loadMore: (numItems: number) => void;
};

const SESSION_HISTORY_PAGE_SIZE = 100;
const DELETE_SELECTION_BATCH_SIZE = 25;
const TRANSCRIPT_METRIC_SAMPLE_LIMIT = 3;
const SUGGESTION_METRIC_SAMPLE_LIMIT = 6;
const POST_METRIC_SAMPLE_LIMIT = 6;

export function SessionManagementPage() {
  const auth = useRoleAuth("operator");
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<SessionStatusFilter>("all");
  const [accidentalOnly, setAccidentalOnly] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const history = usePaginatedQuery(
    convexApi.sessions.getSessionHistoryPage,
    {
      auth,
      status: statusFilter === "all" ? undefined : statusFilter,
      accidentalOnly,
    },
    { initialNumItems: SESSION_HISTORY_PAGE_SIZE },
  ) as PaginatedHistory;
  const setActiveSession = useMutation(convexApi.sessions.setActive);
  const archiveSession = useMutation(convexApi.sessions.archive);
  const deleteSessions = useMutation(convexApi.sessions.deleteLowActivitySessions);

  const rows = history.results ?? [];
  const visibleDeletableIds = useMemo(
    () => rows.filter((row) => row.canDelete).map((row) => row.id),
    [rows],
  );
  const deletableIds = useMemo(() => new Set(visibleDeletableIds), [visibleDeletableIds]);
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedDeletableIds = selectedIds.filter((id) => deletableIds.has(id));
  const allVisibleDeletableSelected =
    visibleDeletableIds.length > 0 && visibleDeletableIds.every((id) => selectedIdSet.has(id));
  const deleteButtonText =
    selectedDeletableIds.length > 0
      ? `Delete selected (${selectedDeletableIds.length})`
      : "Delete selected accidental sessions";

  function toggleVisibleSelection() {
    if (visibleDeletableIds.length === 0) return;
    setSelectedIds((current) => {
      const visibleIds = new Set(visibleDeletableIds);
      const allSelected = visibleDeletableIds.every((id) => current.includes(id));
      if (allSelected) {
        return current.filter((id) => !visibleIds.has(id));
      }
      return [...new Set([...current, ...visibleDeletableIds])];
    });
  }

  async function activate(id: string) {
    await setActiveSession({ auth, sessionId: id as Id<"sessions"> });
    navigate("/admin");
  }

  async function archive(id: string) {
    try {
      await archiveSession({ auth, sessionId: id as Id<"sessions"> });
      notify.success(notificationCopy.admin.sessionArchived);
    } catch {
      notify.error(notificationCopy.admin.couldNotArchiveSession);
    }
  }

  async function deleteSelected() {
    if (selectedDeletableIds.length === 0) return;
    const idsToDelete = [...selectedDeletableIds] as Id<"sessions">[];
    try {
      let deletedCount = 0;
      for (let index = 0; index < idsToDelete.length; index += DELETE_SELECTION_BATCH_SIZE) {
        const result = await deleteSessions({
          auth,
          sessionIds: idsToDelete.slice(index, index + DELETE_SELECTION_BATCH_SIZE),
        });
        deletedCount += result.deleted.length;
      }
      setSelectedIds([]);
      notify.success(sessionsDeletedCopy(deletedCount));
    } catch {
      notify.error(notificationCopy.admin.couldNotDeleteSession);
    }
  }

  async function deleteOne(id: string) {
    try {
      await deleteSessions({ auth, sessionIds: [id as Id<"sessions">] });
      setSelectedIds((current) => current.filter((value) => value !== id));
      notify.success(notificationCopy.admin.sessionDeleted);
    } catch {
      notify.error(notificationCopy.admin.couldNotDeleteSession);
    }
  }

  function toggleRowSelection(row: SessionHistoryItem) {
    if (!row.canDelete) return;
    setSelectedIds((current) =>
      current.includes(row.id)
        ? current.filter((id) => id !== row.id)
        : [...new Set([...current, row.id])],
    );
  }

  function handleRowClick(event: MouseEvent<HTMLElement>, row: SessionHistoryItem) {
    if (isInteractiveClickTarget(event.target)) return;
    toggleRowSelection(row);
  }

  return (
    <div className="operator session-admin">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true" />
          <div className="brand-text">
            <strong title="Conversation Moderator">COMODO</strong>
            <span className="role-badge role-admin" data-testid="role-badge">Session Admin</span>
          </div>
        </div>
        <div className="top-meta">
          <span className="pill on" data-testid="session-history-count">{rows.length} loaded</span>
        </div>
        <div className="top-actions">
          <Link className="btn ghost sm" data-testid="back-to-operator" to="/admin">Admin View</Link>
          <Link className="btn ghost sm" data-testid="open-prompts" to="/admin/llm-settings">LLM Settings</Link>
          <button
            type="button"
            className="btn danger sm"
            data-testid="session-delete-selected"
            disabled={selectedDeletableIds.length === 0}
            onClick={() => void deleteSelected()}
          >
            {deleteButtonText}
          </button>
          <SignOutButton redirectTo="/admin" />
        </div>
      </header>

      <main className="session-admin-main" data-testid="session-history-table">
        <section className="session-admin-hero">
          <div>
            <p className="prompt-key">session history</p>
            <h1>Manage past sessions</h1>
            <p>Open a previous session, archive old work, or filter for empty accidental sessions that are safe to delete.</p>
          </div>
          <div className="session-admin-filters">
            <label>
              <span>Status</span>
              <select
                data-testid="session-status-filter"
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value as SessionStatusFilter);
                  setSelectedIds([]);
                }}
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
                <option value="archived">Archived</option>
                <option value="draft">Draft</option>
              </select>
            </label>
            <label className="switch">
              <input
                type="checkbox"
                data-testid="accidental-only-toggle"
                checked={accidentalOnly}
                onChange={(event) => {
                  setAccidentalOnly(event.target.checked);
                  setSelectedIds([]);
                }}
              />
              <span>Accidental sessions only</span>
            </label>
            <button
              type="button"
              className="btn ghost sm"
              data-testid="session-select-visible"
              disabled={visibleDeletableIds.length === 0}
              onClick={toggleVisibleSelection}
            >
              {allVisibleDeletableSelected ? "Clear visible selection" : "Select all visible"}
            </button>
          </div>
        </section>

        <section className="session-history-list">
          {history.status === "LoadingFirstPage" && <div className="loading">Loading session history...</div>}
          {history.status !== "LoadingFirstPage" && rows.length === 0 && (
            <div className="empty" data-testid="session-history-empty">No matching sessions.</div>
          )}
          {rows.map((row) => (
            <article
              key={row.id}
              className={[
                "session-history-row",
                row.active ? "is-active" : "",
                row.canDelete ? "is-selectable" : "",
                selectedIdSet.has(row.id) ? "is-selected" : "",
              ].filter(Boolean).join(" ")}
              data-testid="session-history-row"
              onClick={(event) => handleRowClick(event, row)}
            >
              <label className="session-history-check" title={row.deleteBlockReason ?? "Safe to delete"}>
                <input
                  type="checkbox"
                  disabled={!row.canDelete}
                  checked={selectedIdSet.has(row.id)}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setSelectedIds((current) =>
                      checked
                        ? row.canDelete ? [...new Set([...current, row.id])] : current
                        : current.filter((id) => id !== row.id),
                    );
                  }}
                />
              </label>
              <div className="session-history-summary">
                <div className="session-history-titleline">
                  <h2>{row.title}</h2>
                  <div className="session-history-badges">
                    <span className={`pill ${row.active ? "on" : "off"}`}>{row.status}</span>
                    <span className="badge">{row.kind}</span>
                    {row.joinCode && <span className="badge mono">#{row.joinCode}</span>}
                    {row.canDelete && <span className="badge warn">accidental candidate</span>}
                  </div>
                </div>
                <div className="session-history-meta">
                  <span>{formatDate(row.startedAt)}</span>
                  {row.endedAt && <span>ended {formatDate(row.endedAt)}</span>}
                  <span>{sessionMetricText(row)}</span>
                </div>
                <div className="session-history-preview">
                  {row.transcriptPreview.length > 0
                    ? row.transcriptPreview.map((line) => (
                        <div key={line} className="session-preview-line">{line}</div>
                      ))
                    : <span className="muted">No transcript yet.</span>}
                </div>
                {!row.canDelete && row.deleteBlockReason && (
                  <div className="session-delete-reason">{row.deleteBlockReason}</div>
                )}
              </div>
              <div className="session-history-actions">
                <button type="button" className="btn primary sm" onClick={() => void activate(row.id)}>
                  Open
                </button>
                {!row.active && (
                  <button type="button" className="btn ghost sm" onClick={() => void archive(row.id)}>
                    Archive
                  </button>
                )}
                {row.canDelete && (
                  <button type="button" className="btn danger sm" onClick={() => void deleteOne(row.id)}>
                    Delete
                  </button>
                )}
              </div>
            </article>
          ))}
        </section>

        {history.status === "CanLoadMore" && (
          <button type="button" className="btn ghost" onClick={() => history.loadMore(SESSION_HISTORY_PAGE_SIZE)}>
            Load more sessions
          </button>
        )}
      </main>
    </div>
  );
}

function formatDate(value: number): string {
  return new Date(value).toLocaleString();
}

function sessionMetricText(row: SessionHistoryItem): string {
  const postCount = row.staffPostCountClamped + row.participantPostCountClamped;
  return [
    `${row.transcriptEntryCountClamped}${row.transcriptEntryCountClamped === TRANSCRIPT_METRIC_SAMPLE_LIMIT ? "+" : ""} transcript`,
    `${row.suggestionCountClamped}${row.suggestionCountClamped === SUGGESTION_METRIC_SAMPLE_LIMIT ? "+" : ""} suggestions`,
    `${postCount}${postCount === POST_METRIC_SAMPLE_LIMIT ? "+" : ""} posts`,
  ].join(" / ");
}

function isInteractiveClickTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && Boolean(target.closest("a, button, input, label, select, textarea"));
}
